import { InstrumentId, InstrumentStatus, VirtualInstrument, PianoEffectsSettings } from './types';
import { ModeledSynthPiano } from './instruments/ModeledSynthPiano';
import { AcousticGrandPiano } from './instruments/AcousticGrandPiano';
import { UprightPianoInstrument } from './instruments/UprightPianoInstrument';
import { ElectricPianoInstrument } from './instruments/ElectricPianoInstrument';
import { EffectsChain } from './effects/EffectsChain';

export class InstrumentManager {
  private effectsChain: EffectsChain;
  private currentId: InstrumentId = 'acoustic-grand';

  private instruments = new Map<InstrumentId, VirtualInstrument>();
  private statuses = new Map<InstrumentId, InstrumentStatus>();
  private listeners = new Set<(status: InstrumentStatus) => void>();

  constructor(ctx: AudioContext, destination: AudioNode) {
    // Connect effects rack (Tone Filter -> Acoustic Reverb -> Destination)
    this.effectsChain = new EffectsChain(ctx, destination);
    const instrumentBus = this.effectsChain.input;

    // Initialize statuses — all 3 sample-based instruments start loading immediately (see eager
    // preload below), so they're marked 'loading' from the start rather than 'idle'.
    this.statuses.set('acoustic-grand', { id: 'acoustic-grand', state: 'loading', progress: 0 });
    this.statuses.set('upright-piano', { id: 'upright-piano', state: 'loading', progress: 0 });
    this.statuses.set('electric-piano', { id: 'electric-piano', state: 'loading', progress: 0 });
    this.statuses.set('synth-piano', { id: 'synth-piano', state: 'ready', progress: 100 });

    // 1. Modeled Rhodes FM Tines Piano (always ready, 0 load, beautiful metallic chime & warm body)
    const synth = new ModeledSynthPiano(ctx, instrumentBus);
    this.instruments.set('synth-piano', synth);

    // 2. Steinway Concert Grand Piano (5 velocity layers, natural decay & hall resonance)
    const acousticGrand = new AcousticGrandPiano(ctx, instrumentBus, {
      onProgress: (pct) => {
        this.updateStatus('acoustic-grand', {
          id: 'acoustic-grand',
          state: pct >= 100 ? 'ready' : 'loading',
          progress: pct,
        });
      },
      onStatusChange: () => {
        const isReady = this.instruments.get('acoustic-grand')?.isReady ?? false;
        this.updateStatus('acoustic-grand', {
          id: 'acoustic-grand',
          state: isReady ? 'ready' : 'error',
          progress: isReady ? 100 : 0,
        });
      },
    });
    this.instruments.set('acoustic-grand', acousticGrand);

    // 3. Yamaha Studio Upright Piano (intimate felted acoustic upright)
    const upright = new UprightPianoInstrument(ctx, instrumentBus, {
      onProgress: (pct) => {
        this.updateStatus('upright-piano', {
          id: 'upright-piano',
          state: pct >= 100 ? 'ready' : 'loading',
          progress: pct,
        });
      },
      onStatusChange: () => {
        const isReady = this.instruments.get('upright-piano')?.isReady ?? false;
        this.updateStatus('upright-piano', {
          id: 'upright-piano',
          state: isReady ? 'ready' : 'error',
          progress: isReady ? 100 : 0,
        });
      },
    });
    this.instruments.set('upright-piano', upright);

    // 4. Vintage Electric Piano (Wurlitzer 200A samples)
    const electric = new ElectricPianoInstrument(ctx, instrumentBus, {
      onProgress: (pct) => {
        this.updateStatus('electric-piano', {
          id: 'electric-piano',
          state: pct >= 100 ? 'ready' : 'loading',
          progress: pct,
        });
      },
      onStatusChange: () => {
        const isReady = this.instruments.get('electric-piano')?.isReady ?? false;
        this.updateStatus('electric-piano', {
          id: 'electric-piano',
          state: isReady ? 'ready' : 'error',
          progress: isReady ? 100 : 0,
        });
      },
    });
    this.instruments.set('electric-piano', electric);

    // Eagerly preload ALL sample-based instruments in the background (not just the default
    // acoustic-grand) — otherwise switching to upright/electric only starts their ~3-5s download
    // on selection, and any note played before it finishes silently falls back to the modeled
    // synth (identical for all 3), making it sound like "instruments don't load, it's always
    // the same Rhodes-ish sound" even though loading eventually succeeds.
    acousticGrand.load().catch(() => {});
    upright.load().catch(() => {});
    electric.load().catch(() => {});

    // Sync initial decay time to all instruments
    const initialDecay = this.effectsChain.getSettings().decayTime;
    for (const inst of this.instruments.values()) {
      inst.setDecayTime?.(initialDecay);
    }
  }

  public getCurrentInstrumentId(): InstrumentId {
    return this.currentId;
  }

  public getStatus(id: InstrumentId): InstrumentStatus {
    return this.statuses.get(id) || { id, state: 'idle' };
  }

  public getAllStatuses(): Map<InstrumentId, InstrumentStatus> {
    return new Map(this.statuses);
  }

  public subscribe(listener: (status: InstrumentStatus) => void): () => void {
    this.listeners.add(listener);
    for (const status of this.statuses.values()) {
      listener(status);
    }
    return () => {
      this.listeners.delete(listener);
    };
  }

  private updateStatus(id: InstrumentId, status: InstrumentStatus) {
    this.statuses.set(id, status);
    for (const listener of this.listeners) {
      try {
        listener(status);
      } catch {
        // ignore
      }
    }
  }

  public setInstrument(id: InstrumentId): void {
    if (this.currentId === id) return;
    this.stopAll();
    this.currentId = id;

    const inst = this.instruments.get(id);
    if (inst && !inst.isReady) {
      const curStatus = this.statuses.get(id);
      if (!curStatus || curStatus.state === 'idle') {
        this.updateStatus(id, { id, state: 'loading', progress: 0 });
      }
      inst.load().catch(() => {});
    }

    const status = this.getStatus(id);
    for (const listener of this.listeners) {
      listener(status);
    }
  }

  public getActiveInstrument(): VirtualInstrument {
    return this.instruments.get(this.currentId) || this.instruments.get('synth-piano')!;
  }

  public playNoteOn(pitch: number, velocity?: number, time?: number): (stopTime?: number) => void {
    return this.getActiveInstrument().playNoteOn(pitch, velocity, time);
  }

  public scheduleNote(pitch: number, velocity: number, startTime: number, durationSeconds: number): void {
    this.getActiveInstrument().scheduleNote(pitch, velocity, startTime, durationSeconds);
  }

  public stopAll(): void {
    for (const inst of this.instruments.values()) {
      inst.stopAll();
    }
  }

  // --- Effects Management ---

  public getEffectsSettings(): PianoEffectsSettings {
    return this.effectsChain.getSettings();
  }

  public updateEffectsSettings(partial: Partial<PianoEffectsSettings>): void {
    this.effectsChain.updateSettings(partial);
    if (partial.decayTime !== undefined) {
      const currentDecay = this.effectsChain.getSettings().decayTime;
      for (const inst of this.instruments.values()) {
        inst.setDecayTime?.(currentDecay);
      }
    }
  }

  public subscribeEffects(listener: (settings: PianoEffectsSettings) => void): () => void {
    return this.effectsChain.subscribe(listener);
  }
}
